import { createLereSurchargeTokenKey, type LereSurchargeTokenPayload } from 'api/reservation'
import { LabFeatureFlags } from 'constants/lab'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { isPropertyRateLoyaltyPackageUpgradeItem } from './loyalty/propertyRateLoyaltyUpgrade'
import moment from 'moment'
import config from 'constants/config'
import type { LereDynamicPricingGroup } from 'lib/lere/types'

type CheckoutOrderItem = Record<string, any>

interface LereSurchargePayloadFields {
  offerId?: string
  roomTypeId?: string
  roomRateId?: string
  checkIn?: string
  checkOut?: string
  lereGroup?: string
}

function isLereDynamicPricingGroup(value: unknown): value is LereDynamicPricingGroup {
  return value === 'A' || value === 'B'
}

export function isEligibleForLereDynamicPricingAbTest(): boolean {
  return config.BRAND === 'luxuryescapes'
}

export function getLereDynamicPricingGroupFromLabFlags(
  labFlags: App.LabFlags | undefined,
  isEligible: () => boolean = isEligibleForLereDynamicPricingAbTest,
): LereDynamicPricingGroup | undefined {
  if (!isEligible()) {
    return undefined
  }

  const flag = labFlags?.[LabFeatureFlags.enableLereDynamicPricingGroup]

  if (!flag?.enabled) {
    return undefined
  }

  return isLereDynamicPricingGroup(flag.variant) ? flag.variant : undefined
}

export function getLereDynamicPricingGroup(state: App.State): LereDynamicPricingGroup | undefined {
  return getLereDynamicPricingGroupFromLabFlags(state.system.features.labFlags)
}

export function getLereDynamicPricingGroupForOffer(
  offer: App.Offer | undefined,
  lereGroup: string | undefined,
): string | undefined {
  return isFlashHotelOffer(offer) && isLereDynamicPricingGroup(lereGroup) ? lereGroup : undefined
}

export function buildLereDynamicPricingSurchargeTokenPayloads(
  hotelOfferViews: Array<App.Checkout.LEAccommodationOfferView>,
  lereGroup: string | undefined,
  postPurchase?: App.CheckoutCartMode,
): Array<LereSurchargeTokenPayload> {
  if (!lereGroup) {
    return []
  }

  return dedupeLereSurchargePayloads(
    hotelOfferViews.flatMap(offerView =>
      offerView.itemViews.flatMap(itemView => {
        const sourcePayload = createLereSurchargePayloadForCheckoutItem(
          itemView.item,
          offerView.offer,
          itemView.roomTypeId,
          lereGroup,
        )
        const targetPackage = getAppliedLoyaltyUpgradeTargetPackage(itemView)
        const targetPayload = createLereSurchargePayloadForLoyaltyUpgradeTarget(
          itemView.item,
          offerView.offer,
          targetPackage,
          lereGroup,
          postPurchase,
        )

        return [sourcePayload, targetPayload]
      }),
    ),
  )
}

function getAppliedLoyaltyUpgradeTargetPackage(
  itemView: App.Checkout.LEAccommodationItemView,
): App.HotelPackage | undefined {
  const upgradeType = itemView.item.loyaltyAppliedUpgradeType
  const availability = itemView.loyaltyUpgradeAvailability

  if (upgradeType === 'room' && availability && 'roomUpgradePackage' in availability) {
    return availability.roomUpgradePackage
  }

  if (upgradeType === 'package' && availability && 'packageUpgradePackage' in availability) {
    return availability.packageUpgradePackage
  }
}

function getResolvableFlashInstantBookingItems(
  cartItems: Array<App.Checkout.LEHotelItem>,
  offers: App.OfferState['offers'],
): Array<App.Checkout.InstantBookingLEHotelItem> | undefined {
  const instantBookingItems = cartItems.filter(isInstantBookingLEHotelItem)
  if (instantBookingItems.length === 0) {
    return []
  }

  if (!instantBookingItems.every(item => !!offers[item.offerId])) {
    return undefined
  }

  return instantBookingItems.filter(item => isFlashHotelOffer(offers[item.offerId]))
}

export function countFlashInstantBookingCartItems(
  cartItems: Array<App.Checkout.LEHotelItem>,
  offers: App.OfferState['offers'],
): number | undefined {
  const flashItems = getResolvableFlashInstantBookingItems(cartItems, offers)
  return flashItems === undefined ? undefined : flashItems.length
}

export function buildLereSurchargeTokenPayloadsFromCartItems(
  cartItems: Array<App.Checkout.LEHotelItem>,
  offers: App.OfferState['offers'],
  lereGroup: string | undefined,
): Array<LereSurchargeTokenPayload> | undefined {
  if (!lereGroup) {
    return []
  }

  const flashItems = getResolvableFlashInstantBookingItems(cartItems, offers)
  if (flashItems === undefined) {
    return undefined
  }

  const payloads: Array<LereSurchargeTokenPayload | undefined> = []

  for (const item of flashItems) {
    const offer = offers[item.offerId]
    const source = getLereDynamicPricingSource(item, offer)

    if (!source) {
      return undefined
    }

    payloads.push(createLereSurchargePayload({
      offerId: offer.id,
      ...source,
      checkIn: item.checkIn,
      checkOut: item.checkOut,
      lereGroup,
    }))
  }

  return dedupeLereSurchargePayloads(payloads)
}

function dedupeLereSurchargePayloads(
  payloads: Iterable<LereSurchargeTokenPayload | undefined>,
): Array<LereSurchargeTokenPayload> {
  const payloadsByKey = new Map<string, LereSurchargeTokenPayload>()

  for (const payload of payloads) {
    if (payload) {
      payloadsByKey.set(createLereSurchargeTokenKey(payload), payload)
    }
  }

  return Array.from(payloadsByKey.values())
}

function createLereSurchargePayload(
  fields: LereSurchargePayloadFields,
): LereSurchargeTokenPayload | undefined {
  if (!fields.offerId || !fields.roomTypeId || !fields.roomRateId || !fields.checkIn || !fields.checkOut || !fields.lereGroup) {
    return undefined
  }

  return {
    offerId: fields.offerId,
    roomTypeId: fields.roomTypeId,
    roomRateId: fields.roomRateId,
    checkIn: moment.utc(fields.checkIn).format(ISO_DATE_FORMAT),
    checkOut: moment.utc(fields.checkOut).format(ISO_DATE_FORMAT),
    lereGroup: fields.lereGroup,
  }
}

export function getLereDynamicPricingSource(
  item: App.Checkout.LEAccommodationItem,
  offer: App.Offer,
): Pick<LereSurchargeTokenPayload, 'roomTypeId' | 'roomRateId'> | undefined {
  if (!isInstantBookingLEHotelItem(item)) {
    return undefined
  }

  if (item.dynamicPricingRoomTypeId && item.dynamicPricingRoomRateId) {
    return {
      roomTypeId: item.dynamicPricingRoomTypeId,
      roomRateId: item.dynamicPricingRoomRateId,
    }
  }

  const pkg = offer.packages.find(
    offerPackage =>
      offerPackage.id === item.packageId &&
      offerPackage.duration === item.duration &&
      offerPackage.roomRate?.id === item.roomRateId,
  )

  return pkg?.roomType?.id && pkg.roomRate?.id ? {
    roomTypeId: pkg.roomType.id,
    roomRateId: pkg.roomRate.id,
  } : undefined
}

export function createLereSurchargePayloadForCheckoutItem(
  item: App.Checkout.LEAccommodationItem,
  offer: App.Offer | undefined,
  roomTypeId: string | undefined,
  lereGroup: string | undefined,
): LereSurchargeTokenPayload | undefined {
  if (
    !isInstantBookingLEHotelItem(item) ||
    !isFlashHotelOffer(offer)
  ) {
    return undefined
  }

  if (item.pricing?.dynamicPricingEligible === false) {
    return undefined
  }

  const source = getLereDynamicPricingSource(item, offer)

  return createLereSurchargePayload({
    offerId: offer.id,
    roomTypeId: source?.roomTypeId ?? roomTypeId,
    roomRateId: source?.roomRateId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    lereGroup,
  })
}

export function createLereSurchargePayloadForLoyaltyUpgradeTarget(
  item: App.Checkout.LEAccommodationItem,
  offer: App.Offer | undefined,
  targetPackage: App.HotelPackage | undefined,
  lereGroup: string | undefined,
  postPurchase?: App.CheckoutCartMode,
): LereSurchargeTokenPayload | undefined {
  if (
    postPurchase ||
    !item.loyaltyAppliedUpgradeType ||
    isPropertyRateLoyaltyPackageUpgradeItem(item) ||
    !isInstantBookingLEHotelItem(item) ||
    !isFlashHotelOffer(offer)
  ) {
    return undefined
  }

  return createLereSurchargePayload({
    offerId: offer.id,
    roomTypeId: targetPackage?.roomType.id,
    roomRateId: targetPackage?.roomRate.id,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    lereGroup,
  })
}

export function getLereDynamicPricingSurchargeAmountForCheckoutItem(
  item: App.Checkout.LEAccommodationItem,
  offer: App.Offer | undefined,
  roomTypeId: string | undefined,
  lereGroup: string | undefined,
  lereSurchargeTokens: App.OfferState['lereSurchargeTokens'],
): number | undefined {
  const token = getLereDynamicPricingSurchargeTokenForCheckoutItem(
    item,
    offer,
    roomTypeId,
    lereGroup,
    lereSurchargeTokens,
  )

  return token?.status === 'success' ? token.surchargeAmount : undefined
}

export function getLereDynamicPricingSurchargeTokenForCheckoutItem(
  item: App.Checkout.LEAccommodationItem,
  offer: App.Offer | undefined,
  roomTypeId: string | undefined,
  lereGroup: string | undefined,
  lereSurchargeTokens: App.OfferState['lereSurchargeTokens'],
): App.LereSurchargeToken | undefined {
  const payload = createLereSurchargePayloadForCheckoutItem(item, offer, roomTypeId, lereGroup)
  const tokenKey = payload ? createLereSurchargeTokenKey(payload) : undefined

  return tokenKey ? lereSurchargeTokens[tokenKey] : undefined
}

export function getLereDynamicPricingSurchargeTokenForStay(
  fields: LereSurchargePayloadFields,
  lereSurchargeTokens: App.OfferState['lereSurchargeTokens'],
): App.LereSurchargeToken | undefined {
  const payload = createLereSurchargePayload(fields)
  const tokenKey = payload ? createLereSurchargeTokenKey(payload) : undefined

  return tokenKey ? lereSurchargeTokens[tokenKey] : undefined
}

function getOrderItemStayDates(item: CheckoutOrderItem): Pick<LereSurchargePayloadFields, 'checkIn' | 'checkOut'> | undefined {
  if (typeof item.check_in !== 'string' || typeof item.check_out !== 'string') {
    return undefined
  }

  return {
    checkIn: item.check_in,
    checkOut: item.check_out,
  }
}

function getOrderItemOfferAndRoomType(
  item: CheckoutOrderItem,
  hotelOfferViews: Array<App.Checkout.LEAccommodationOfferView>,
): Pick<LereSurchargePayloadFields, 'offerId' | 'roomTypeId' | 'roomRateId'> | undefined {
  if (typeof item.cart_item_id === 'string') {
    const offerView = hotelOfferViews.find(view =>
      isFlashHotelOffer(view.offer) &&
      view.itemViews.some(itemView => itemView.item.itemId === item.cart_item_id),
    )
    const itemView = offerView?.itemViews.find(itemView => itemView.item.itemId === item.cart_item_id)
    if (
      item.offer_booking_type === 'reservation' &&
      item.reservation_type === 'instant_booking' &&
      item.applied_upgrade_type &&
      itemView?.item.loyaltyAppliedUpgradeType &&
      !isPropertyRateLoyaltyPackageUpgradeItem(itemView.item)
    ) {
      // The order books the upgraded package; its surcharge must match the target total
      // offset by the customer payment plus the loyalty upgrade payment.
      const targetPackage = getAppliedLoyaltyUpgradeTargetPackage(itemView)
      return targetPackage && offerView ? {
        offerId: offerView.offerId,
        roomTypeId: targetPackage.roomType.id,
        roomRateId: targetPackage.roomRate.id,
      } : undefined
    }
    const source = offerView?.offer && itemView ? getLereDynamicPricingSource(itemView.item, offerView.offer) : undefined

    if (offerView && source) {
      return { offerId: offerView.offerId, ...source }
    }
  }

  if (item.offer_booking_type === 'reservation') {
    if (
      typeof item.offer_id !== 'string' ||
      typeof item.package_id !== 'string' ||
      typeof item.room_rate_id !== 'string' ||
      typeof item.number_of_nights !== 'number'
    ) {
      return undefined
    }

    const offerView = hotelOfferViews.find(view => view.offerId === item.offer_id && isFlashHotelOffer(view.offer))
    // Formatted order items only carry package/rate IDs; the token needs room type.
    const packageUniqueKey = getPackageUniqueKey(item.package_id, item.number_of_nights, item.room_rate_id)
    const pkg = offerView?.offer?.packages.find(pkg => pkg.uniqueKey === packageUniqueKey)

    return {
      offerId: item.offer_id,
      roomTypeId: pkg?.roomType?.id,
      roomRateId: pkg?.roomRate?.id,
    }
  }

  if (item.type !== 'reservation' || typeof item.cart_item_id !== 'string') {
    return undefined
  }

  // BNBL append items only reference the existing cart item, so recover offer/room data from item views.
  const offerView = hotelOfferViews.find(view =>
    isFlashHotelOffer(view.offer) &&
    view.itemViews.some(itemView => itemView.item.itemId === item.cart_item_id),
  )
  const itemView = offerView?.itemViews.find(itemView => itemView.item.itemId === item.cart_item_id)

  if (!offerView || !itemView) {
    return undefined
  }

  return {
    offerId: offerView.offerId,
    roomTypeId: itemView.roomTypeId,
    roomRateId: itemView.pkg?.roomRate?.id,
  }
}

export function createLereSurchargePayloadForOrderItem(
  item: CheckoutOrderItem,
  hotelOfferViews: Array<App.Checkout.LEAccommodationOfferView>,
  lereGroup: string | undefined,
): LereSurchargeTokenPayload | undefined {
  const stayDates = getOrderItemStayDates(item)
  const offerAndRoomType = getOrderItemOfferAndRoomType(item, hotelOfferViews)

  if (!stayDates || !offerAndRoomType) {
    return undefined
  }

  return createLereSurchargePayload({
    ...offerAndRoomType,
    ...stayDates,
    lereGroup,
  })
}
